import React, { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Menu, MenuItem, ListItemIcon, ListItemText, Divider } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import ReplayIcon from '@mui/icons-material/Replay';
import { BalanceDomino } from 'repository/timekeeper/@types/BalanceDomino';
import { CreateBalanceDomino } from 'components/dialog/balance_domino/create';
import { UpdateBalanceDomino } from 'components/dialog/balance_domino/update';
import {
    buildSectionSubpageActionPath,
    buildSectionSubpageBasePath,
    parseSectionSubpageActionRoute,
} from 'utils/sectionSubpageRouting';

interface SubMenuProps {
    selected: BalanceDomino[];
    cleanSelected: () => void;
    rows: BalanceDomino[];
    contextMenu: any;
    setContextMenu: (value: any) => void;
    reloading: () => void;
}

const SubMenu: FC<SubMenuProps> = ({ selected, cleanSelected, rows, contextMenu, setContextMenu, reloading }) => {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();

    const [openAdd, setOpenAdd] = useState(false);
    const [openEdit, setOpenEdit] = useState(false);

    const modalRoute = parseSectionSubpageActionRoute(location.pathname, 'accruals', 'balance_domino', ['add', 'update']);
    const routeSelected = useMemo(() => {
        if (!modalRoute.id) return null;
        return rows.find((row) => String(row?.balance_domino_id) === String(modalRoute.id)) || null;
    }, [modalRoute.id, rows]);
    const selectedForDialog = routeSelected ? [routeSelected] : selected;

    const closeToBase = () => {
        const basePath = buildSectionSubpageBasePath('accruals', 'balance_domino');
        if (modalRoute.action) {
            navigate(basePath);
            return;
        }

        setOpenAdd(false);
        setOpenEdit(false);
    };

    interface Option {
        name: string;
        icon: React.ReactNode;
        click: () => void;
        color: string;
        divider: boolean;
        disabled: boolean;
        selected: boolean;
        onlyOne: boolean;
        hidden: boolean;
        dialog?: React.ReactNode | null;
    }

    const options: Option[] = [
        {
            name: t('accruals.accrual_codes.context_menu.add'),
            icon: <AddIcon />,
            click: () => {
                navigate(buildSectionSubpageActionPath('accruals', 'balance_domino', 'add'));
                setContextMenu(null);
            },
            color: '',
            divider: false,
            disabled: false,
            selected: false,
            onlyOne: false,
            hidden: !(JSON.parse(localStorage.getItem('permissions') || '[]') as string[])
                .find((p: string) => p === 'timekeeper.CreateBalanceDomino'),
            dialog: <CreateBalanceDomino open={modalRoute.action === 'add' || openAdd} setOpen={(value: boolean) => { if (!value) { closeToBase(); } else { setOpenAdd(true); } }} rows={rows} reloading={reloading} />
        },
        {
            name: t('accruals.accrual_codes.context_menu.edit'),
            icon: <EditIcon />,
            click: () => {
                if (!selected?.[0]?.balance_domino_id) return;
                navigate(buildSectionSubpageActionPath('accruals', 'balance_domino', 'update', selected[0].balance_domino_id));
                setContextMenu(null);
            },
            color: '',
            divider: false,
            disabled: selected.length !== 1,
            selected: false,
            onlyOne: true,
            hidden: selected.length !== 1 || !(JSON.parse(localStorage.getItem('permissions') || '[]') as string[])
                .find((p: string) => p === 'timekeeper.PatchBalanceDominoById'),
            dialog: <UpdateBalanceDomino open={modalRoute.action === 'update' || openEdit} setOpen={(value: boolean) => { if (!value) { closeToBase(); } else { setOpenEdit(true); } }} rows={rows} rowToEdit={selectedForDialog[0]} reloading={reloading} cleanSelected={cleanSelected} />
        },
        {
            name: t('accruals.accrual_codes.context_menu.reload'),
            icon: <ReplayIcon />,
            click: () => {
                reloading();
                setContextMenu(null);
            },
            color: '',
            divider: true,
            disabled: false,
            selected: false,
            onlyOne: false,
            hidden: false,
            dialog: null
        }
    ];

    return (
        <>
            <Menu
                open={Boolean(contextMenu)}
                onClose={() => setContextMenu(null)}
                anchorReference="anchorPosition"
                anchorPosition={contextMenu ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined}
            >
                {options.map((option) => (
                    <div key={option.name} hidden={option.onlyOne && selected?.length !== 1}>
                        {option.divider && <Divider />}
                        <MenuItem onClick={option.click} disabled={option.disabled} >
                            <ListItemIcon>{option.icon}</ListItemIcon>
                            <ListItemText primary={option.name} slotProps={{ primary: { color: option.color } }} />
                        </MenuItem>
                    </div>
                ))}
            </Menu>
            {options.map((option) => (
                option.dialog && <React.Fragment key={`dialog-${option.name}`}>{option.dialog}</React.Fragment>
            ))}
        </>
    );
};

export { SubMenu };
